import { getCharacterAsset } from "@/assets/characters";

type Props = {
  code: string | null | undefined;
  /** Database `avatar_url` — used when no bundled asset exists for this code. */
  fallbackUrl?: string | null;
  alt: string;
  className?: string;
  /** Lazy by default; pass "eager" for above-the-fold (e.g. character detail hero). */
  loading?: "eager" | "lazy";
  decoding?: "async" | "sync" | "auto";
  sizes?: string;
  /** Hint the browser this is the LCP candidate. Use sparingly — one per page. */
  fetchPriority?: "high" | "low" | "auto";
};

/**
 * Renders a character portrait using bundled WebP + JPG fallback when
 * available, otherwise falls back to the database avatar URL.
 *
 * This is the single component every surface (cards, hero, chips,
 * emails-preview, rewards) should use to render a character image —
 * it keeps the visual identity consistent and the format optimized.
 */
export function CharacterImage({
  code,
  fallbackUrl,
  alt,
  className,
  loading = "lazy",
  decoding = "async",
  sizes,
  fetchPriority,
}: Props) {
  const asset = getCharacterAsset(code);

  if (asset) {
    return (
      <picture>
        <source srcSet={asset.webp} type="image/webp" />
        <img
          src={asset.jpg}
          alt={alt}
          className={className}
          loading={loading}
          decoding={decoding}
          sizes={sizes}
          fetchPriority={fetchPriority}
          width={512}
          height={512}
        />
      </picture>
    );
  }

  if (fallbackUrl) {
    return (
      <img
        src={fallbackUrl}
        alt={alt}
        className={className}
        loading={loading}
        decoding={decoding}
        sizes={sizes}
        fetchPriority={fetchPriority}
      />
    );
  }

  return null;
}
